import { IconAlert } from '@consta/icons/IconAlert'
import { IconAllDone } from '@consta/icons/IconAllDone'
import { IconInfo } from '@consta/icons/IconInfo'
import { Combobox } from '@consta/uikit/Combobox'
import { withTooltip } from '@consta/uikit/withTooltip'
import { getSimpleTooltipContent } from '@core/core/helpers'
import type { MappedEntityType, WellListItem } from '@core/core/types'
import type { Nullable, Prettify } from 'common/types'
import { VirtualTable } from 'rc-table'
import type { ColumnType } from 'rc-table/lib/interface'
import type { FC } from 'react'
import { useCallback, useMemo } from 'react'
import useResizeObserver from 'use-resize-observer/polyfilled'

import type { WellMappingTableProps } from './types'
import css from './WellMappingTable.css'

type WellRowType = Prettify<
  MappedEntityType & {
    id: string
  }
>

const IconManual = withTooltip({
  content: getSimpleTooltipContent('Скважина была привязана вручную'),
})(IconInfo)

const renderStatusCellCallback = (_: string, row: WellRowType) => {
  switch (row.status) {
    case 'notmapped': {
      return <IconAlert className={css.mappingStatusIcon} size="s" view="alert" />
    }
    case 'success': {
      return <IconAllDone className={css.mappingStatusIcon} size="s" view="success" />
    }
    case 'error': {
      return <IconAlert className={css.mappingStatusIcon} size="s" view="alert" />
    }
    case 'manual': {
      return <IconManual className={css.mappingStatusIcon} size="s" view="secondary" />
    }
    default: {
      return null
    }
  }
}

export const WellMappingTable: FC<WellMappingTableProps> = ({
  mappedWells,
  wellsForMapping,
  mapWellValue,
}) => {
  const wellRows: WellRowType[] = useMemo(
    () =>
      mappedWells.map((mappedWell, index) => ({
        id: index.toFixed(),
        ...mappedWell,
      })),
    [mappedWells],
  )

  const fields = useMemo(
    () => [...new Set(wellsForMapping.map(value => value.fieldName))],
    [wellsForMapping],
  )

  const setComboboxValue = useCallback(
    (row: WellRowType, value: Nullable<WellListItem>) => {
      mapWellValue({
        name: row.name,
        status: value ? 'manual' : 'notmapped',
        mappedTo: value ? value : undefined,
      })
    },
    [mapWellValue],
  )

  const renderNameInProjectCellCallback = useCallback(
    (_: string, row: WellRowType) => {
      return (
        <Combobox
          size="m"
          view="clear"
          items={wellsForMapping}
          value={row.mappedTo}
          getItemKey={item => item.id}
          getItemLabel={item => item.wellName}
          placeholder="Не привязано"
          labelForEmptyItems="Нет скважин в проекте"
          labelForNotFound="Не найдено"
          groups={fields}
          getItemGroupKey={item => item.fieldName}
          getGroupKey={group => group}
          getGroupLabel={group => group}
          onChange={value => {
            setComboboxValue(row, value)
          }}
        />
      )
    },
    [wellsForMapping, fields, setComboboxValue],
  )

  const wellColumns: ColumnType<WellRowType>[] = useMemo(
    () => [
      {
        title: 'Имя скважины в файле',
        key: 'name',
        dataIndex: 'name',
        width: '40%',
        className: css.sourceWellColumn,
      },
      {
        title: 'Статус',
        key: 'status',
        dataIndex: 'status',
        width: '15%',
        align: 'center',
        render: renderStatusCellCallback,
      },
      {
        title: 'Имя скважины в проекте',
        key: 'mappedTo',
        dataIndex: 'mappedTo',
        render: renderNameInProjectCellCallback,
        width: '45%',
        className: css.mappedWellColumn,
        onCell: (data: WellRowType) => {
          return !data.mappedTo ? { className: css.mappingUnmapped } : {}
        },
      },
    ],
    [renderNameInProjectCellCallback],
  )

  const {
    ref,
    width = 650,
    height = 650,
  } = useResizeObserver<HTMLDivElement>({ round: x => Math.floor(x) })
  const scrollArea = useMemo(() => {
    const border = 1
    const headerHeight = 40 + 3 * border
    return { x: width - 2 * border, y: height - headerHeight }
  }, [width, height])

  return (
    <div ref={ref} className={css.mappingTableContainer}>
      <VirtualTable<WellRowType>
        className={css.mappingTable}
        data={wellRows}
        rowKey="id"
        columns={wellColumns}
        showHeader={true}
        scroll={scrollArea}
        rowClassName={css.mappingTableRow}
      />
    </div>
  )
}
